<template>
  <v-container fluid>
    <v-row align="center" justify="center" no-gutters>
      <v-col cols="12">
        <v-toolbar color="white" flat>
          <v-toolbar-title class="text-h5">用户管理</v-toolbar-title>
        </v-toolbar>
      </v-col>
      <v-col cols="12">
        <v-card outlined>
          <v-card-actions class="pb-0 px-4">
            <v-subheader class="px-0">用户列表</v-subheader>
            <v-spacer></v-spacer>
            <v-btn outlined small color="primary" @click.stop="newItem">新增</v-btn>
          </v-card-actions>

          <v-card-text>
            <v-data-table
              :loading="loading"
              :headers="headers"
              :items="userList"
              :items-per-page="20"
              :footer-props="{itemsPerPageOptions: [10,20,50,100]}"
            >
              <template v-slot:item.username="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.username"
                  cancel-text="取消"
                  save-text="保存"
                  large
                  persistent
                  @save="saveProps({id: item.id, username: item.username})"
                >
                  <div>{{item.username}}</div>
                  <template v-slot:input>
                    <v-text-field
                      class="pt-6"
                      v-model="item.username"
                      :rules="[notEmpty, max32chars, codeFormat]"
                      label="用户名"
                      outlined
                      counter="32"
                      autofocus
                    ></v-text-field>
                  </template>
                </v-edit-dialog>
              </template>

              <template v-slot:item.nickname="{ item }">
                <v-edit-dialog
                  :return-value.sync="item.nickname"
                  cancel-text="取消"
                  save-text="保存"
                  large
                  persistent
                  @save="saveProps({id: item.id, nickname: item.nickname})"
                >
                  <div>{{item.nickname}}</div>
                  <template v-slot:input>
                    <v-text-field
                      class="pt-6"
                      v-model="item.nickname"
                      :rules="[notEmpty, max32chars]"
                      label="昵称"
                      outlined
                      counter="32"
                      autofocus
                    ></v-text-field>
                  </template>
                </v-edit-dialog>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip :color="statusColor(item.status)" dark @click.stop="changeStatus(item)">
                  {{ item.status | statusFilter }}
                </v-chip>
              </template>

              <template v-slot:item.createTime="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{processDate(item.createTime).distance}}
                    </span>
                  </template>
                  <span>{{processDate(item.createTime).time}}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.updateTime="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{processDate(item.updateTime).distance}}
                    </span>
                  </template>
                  <span>{{processDate(item.updateTime).time}}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small class="mr-2" @click.stop="editRole(item)">mdi-pencil</v-icon>
                    </span>
                  </template>
                  <span>修改</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-icon small @click="openDelete(item)">mdi-delete</v-icon>
                    </span>
                  </template>
                  <span>删除</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog.open" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">新增用户</span>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-form v-model="editDialog.valid" ref="form">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    :rules="[notEmpty, max32chars, codeFormat]"
                    label="用户名"
                    counter="32"
                    autofocus
                    dense
                    v-model="editDialog.username"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    :rules="[notEmpty, max32chars]"
                    label="昵称"
                    counter="32"
                    dense
                    v-model="editDialog.nickname"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    :rules="[notEmpty, max32chars, min, lowerCase, upperCase, symbol]"
                    label="密码"
                    counter="32"
                    dense
                    v-model="editDialog.password"
                    :append-icon="editDialog.show ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="editDialog.show ? 'text' : 'password'"
                    @click:append="editDialog.show = !editDialog.show"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="editDialog.open = false">取消</v-btn>
          <v-btn color="blue darken-1" text :loading="editDialog.loading" :disabled="editDialog.loading"
                 @click="saveItem">保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">删除用户</span>
        </v-card-title>
        <v-card-text class="pb-0">
          是否确认要删除：<br>
          {{ deleteDialog.item.nickname }} （{{ deleteDialog.item.username }}）
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="deleteDialog.open = false">取消</v-btn>
          <v-btn color="error" text :loading="deleteDialog.loading" :disabled="deleteDialog.loading"
                 @click="deleteUser">删除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="roleDialog.open" fullscreen hide-overlay transition="dialog-bottom-transition">
      <edit-user-dialog
        @alert="showAlert"
        @close="roleDialog.open = false"
        @save="saveUserRole"
        :user-data="roleDialog.item"
        :status="roleDialog.open"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { buildAxios } from '@/plugins/axios'
import { parseISO, format, formatDistanceToNow } from 'date-fns'
import zhCN from 'date-fns/locale/zh-CN'
import { handleResponse } from '@/plugins/errorHandler'
import EditUserDialog from '@/components/EditUserDialog'

export default {
  name: 'User',
  components: { EditUserDialog },
  data () {
    return {
      loading: true,
      headers: [
        {
          text: 'ID',
          value: 'id'
        },
        {
          text: '用户名',
          value: 'username'
        },
        {
          text: '昵称',
          value: 'nickname'
        },
        {
          text: '用户状态',
          value: 'status'
        },
        {
          text: '创建时间',
          value: 'createTime'
        },
        {
          text: '修改时间',
          value: 'updateTime'
        },
        {
          text: '操作',
          value: 'actions',
          sortable: false
        }
      ],
      userList: [],
      notEmpty: v => !!v || '不能为空',
      max32chars: v => v.length <= 32 || '不能超过32个字符',
      codeFormat: v => /^[A-Za-z0-9_\\-]+$/.test(v) || '只能包含字母数字和 - _',
      min: v => v.length >= 12 || '密码至少12个字符',
      lowerCase: v => /.*[a-z].*/.test(v) || '至少包含一个小写字母',
      upperCase: v => /.*[A-Z].*/.test(v) || '至少包含一个大写字母',
      symbol: v => /.*[[\]!@#$%^&*()_+\-=`~;:'"{},.<>/?|\\].*/.test(v) || '至少包含一个符号',
      editDialog: {
        open: false,
        valid: true,
        loading: false,
        username: '',
        nickname: '',
        password: '',
        show: false
      },
      deleteDialog: {
        open: false,
        loading: false,
        item: {}
      },
      roleDialog: {
        open: false,
        item: {}
      }
    }
  },
  created () {
    this.init()
  },
  methods: {
    showAlert (arg) {
      this.$emit('alert', arg)
    },
    init () {
      const request = buildAxios()
      request.get('user/getAll')
        .then(res => {
          this.userList = res.data.data
          this.loading = false
        })
        .catch(reason => {
          this.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
      this.$store.dispatch('getRoles')
    },
    statusColor (value) {
      return value === 0 ? 'red' : 'green'
    },
    processDate (date) {
      const time = parseISO(date)
      return {
        distance: formatDistanceToNow(time, { locale: zhCN }) + '前',
        time: format(time, 'yyyy-MM-dd HH:mm:ss')
      }
    },
    openDelete (item) {
      this.deleteDialog.item = item
      this.deleteDialog.open = true
    },
    deleteUser () {
      this.deleteDialog.loading = true
      const request = buildAxios()
      request.post('user/delete', this.deleteDialog.item)
        .then(res => {
          const du = res.data.data
          this.userList = this.userList.filter(u => u.id !== du.id)
          this.$emit('alert', {
            msg: '用户删除成功！',
            type: 'success'
          })
          this.deleteDialog.open = false
          this.deleteDialog.loading = false
        })
        .catch(reason => {
          this.deleteDialog.loading = false
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    saveProps (arg) {
      const request = buildAxios()
      request.post('user/modify', arg)
        .then(() => {
          this.$emit('alert', {
            msg: '修改成功！',
            type: 'success'
          })
        })
        .catch(reason => {
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    changeStatus (item) {
      const payload = {
        id: item.id,
        status: item.status ? 0 : 1
      }

      const request = buildAxios()
      request.post('user/modify', payload)
        .then(res => {
          const ru = res.data.data
          const idx = this.userList.findIndex(u => u.id === ru.id)
          this.$set(this.userList, idx, ru)

          this.$emit('alert', {
            msg: ru.status ? '已启用' : '已禁用',
            type: 'success'
          })
        })
        .catch(reason => {
          this.$emit('alert', {
            msg: handleResponse(reason.response),
            type: 'error'
          })
        })
    },
    newItem () {
      this.editDialog.open = true
      this.editDialog.username = ''
      this.editDialog.nickname = ''
      this.editDialog.password = ''
      this.editDialog.show = false
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    saveItem () {
      this.$refs.form.validate()
      if (this.editDialog.valid) {
        this.editDialog.loading = true
        const request = buildAxios()
        request.post('user/add', this.editDialog)
          .then(res => {
            this.userList.push(res.data.data)
            this.$emit('alert', {
              msg: '用户添加成功！',
              type: 'success'
            })
            this.editDialog.open = false
            this.editDialog.loading = false
          })
          .catch(reason => {
            this.editDialog.loading = false
            this.$emit('alert', {
              msg: handleResponse(reason.response),
              type: 'error'
            })
          })
      }
    },
    editRole (item) {
      this.roleDialog.item = item
      this.roleDialog.open = true
    },
    saveUserRole (arg) {
      const idx = this.userList.findIndex(u => u.id === arg.id)
      this.$set(this.userList, idx, arg)

      this.roleDialog.open = false
      this.$emit('alert', {
        msg: '用户角色修改成功！',
        type: 'success'
      })
    }
  },
  filters: {
    statusFilter: value => {
      return value === 0 ? '未启用' : '已启用'
    }
  }
}
</script>
